import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Mail, RefreshCw, Search, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { reportUiError } from '../../sentry';
import { useNavigate } from 'react-router-dom';
import { channelLabel, channelsToArray } from '../../lib/channels';
import { isUpcomingScheduledFor } from '../../lib/newsletterSchedule';
import { newsletterStatusLabel } from '../../lib/newsletterStatus';

type OutgoingNewsletterRow = Record<string, unknown> & {
  id?: string | number;
  name?: string | null;
  subtitle?: string | null;
  status?: string | null;
  channels?: unknown;
  scheduled_for?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

const SEARCH_STORAGE_KEY = 'outgoing-newsletters:search';

function bestName(row: OutgoingNewsletterRow): string {
  const name = typeof row.name === 'string' ? row.name.trim() : '';
  if (name) return name;
  return '';
}

function formatDate(val: unknown): string {
  if (typeof val !== 'string' || !val) return '—';
  try {
    return new Date(val).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return val;
  }
}

export default function OutgoingNewslettersView() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<OutgoingNewsletterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(
    () => sessionStorage.getItem(SEARCH_STORAGE_KEY) ?? ''
  );
  /** Lowercased status key, or null for "all statuses". */
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  /** "all" shows everything; "upcoming" shows only items scheduled today or later. */
  const [timeFilter, setTimeFilter] = useState<'all' | 'upcoming'>('all');

  const fetchRows = async () => {
    setLoading(true);
    try {
      const ordered = await supabase
        .from('newsletters')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1000);

      if (!ordered.error) {
        setRows((ordered.data ?? []) as OutgoingNewsletterRow[]);
        return;
      }

      const fallback = await supabase.from('newsletters').select('*').limit(1000);
      if (fallback.error) throw fallback.error;
      setRows((fallback.data ?? []) as OutgoingNewsletterRow[]);
    } catch (e) {
      reportUiError('newsletters', 'list', e);
      const message = e instanceof Error ? e.message : 'Error fetching newsletters';
      toast.error(message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchRows();
  }, []);

  useEffect(() => {
    if (searchQuery) sessionStorage.setItem(SEARCH_STORAGE_KEY, searchQuery);
    else sessionStorage.removeItem(SEARCH_STORAGE_KEY);
  }, [searchQuery]);

  /**
   * Map of lowercase-status -> { label, count }. We keep the original casing
   * (e.g. "Draft", "On Hold") for display while filtering case-insensitively.
   */
  const statusCounts = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>();
    for (const r of rows) {
      const raw = typeof r.status === 'string' ? r.status.trim() : '';
      const key = raw.toLowerCase() || 'unknown';
      const label = raw || 'Unknown';
      const existing = counts.get(key);
      if (existing) existing.count += 1;
      else counts.set(key, { label, count: 1 });
    }
    return Array.from(counts.entries())
      .map(([key, v]) => ({ key, label: v.label, count: v.count }))
      .sort((a, b) => b.count - a.count);
  }, [rows]);

  useEffect(() => {
    if (statusFilter && !statusCounts.some((s) => s.key === statusFilter)) {
      setStatusFilter(null);
    }
  }, [statusFilter, statusCounts]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (timeFilter === 'upcoming' && !isUpcomingScheduledFor(r.scheduled_for)) return false;
      if (statusFilter) {
        const rowStatus = typeof r.status === 'string' ? r.status.trim().toLowerCase() : '';
        const key = rowStatus || 'unknown';
        if (key !== statusFilter) return false;
      }
      if (!q) return true;
      const name = bestName(r).toLowerCase();
      const status = typeof r.status === 'string' ? r.status.toLowerCase() : '';
      const id = r.id != null ? String(r.id).toLowerCase() : '';
      return name.includes(q) || status.includes(q) || id.includes(q);
    });
  }, [rows, searchQuery, statusFilter, timeFilter]);

  const upcomingCount = useMemo(
    () => rows.filter((r) => isUpcomingScheduledFor(r.scheduled_for)).length,
    [rows]
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Mail className="w-8 h-8 mr-3 text-emerald-500" />
            Content
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Drafts and queued sends.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1">
              <button
                type="button"
                onClick={() => setTimeFilter('all')}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  timeFilter === 'all'
                    ? 'bg-emerald-500 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                All
                <span className="ml-1 opacity-80">{rows.length}</span>
              </button>
              <button
                type="button"
                onClick={() => setTimeFilter('upcoming')}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  timeFilter === 'upcoming'
                    ? 'bg-emerald-500 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Upcoming
                <span className="ml-1 opacity-80">{upcomingCount}</span>
              </button>
            </div>

            {statusCounts.length > 0 ? (
              <>
                <span className="text-xs text-gray-400 dark:text-gray-500">·</span>
                <button
                  type="button"
                  onClick={() => setStatusFilter(null)}
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                    statusFilter === null
                      ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  Any status
                </button>
                {statusCounts.map((s) => {
                  const active = statusFilter === s.key;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setStatusFilter(active ? null : s.key)}
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                        active
                          ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {s.label}
                      <span className={`ml-1 ${active ? '' : 'text-gray-500 dark:text-gray-400'}`}>{s.count}</span>
                    </button>
                  );
                })}
              </>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search…"
              className="pl-10 pr-9 py-2.5 w-64 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-shadow"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                title="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => navigate('/outgoing-newsletters/new')}
            className="inline-flex items-center justify-center p-2.5 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600"
            title="New content"
            aria-label="New content"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => void fetchRows()}
            disabled={loading}
            className="inline-flex items-center justify-center p-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {['Name', 'Status', 'Audience', 'Created'].map((header) => (
                  <th
                    key={header}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-emerald-500 mx-auto" />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-gray-500 dark:text-gray-400">
                    No content found
                  </td>
                </tr>
              ) : (
                filtered.map((r, idx) => {
                  const channelKeys = channelsToArray(r.channels);
                  return (
                    <tr
                      key={String(r.id ?? idx)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => {
                        if (r.id == null) return;
                        navigate(`/outgoing-newsletters/${String(r.id)}/content`);
                      }}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {bestName(r) || '—'}
                        </div>
                        {typeof r.subtitle === 'string' && r.subtitle.trim() ? (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{r.subtitle.trim()}</div>
                        ) : null}
                        {r.id != null ? (
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 font-mono mt-1">{String(r.id)}</div>
                        ) : null}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          {newsletterStatusLabel(r.status as string | null | undefined)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {channelKeys.length === 0 ? (
                          <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            All subscribers
                          </span>
                        ) : (
                          <div
                            className="flex flex-wrap gap-1"
                            title={`Sends to subscribers in: ${channelKeys
                              .map((k) => channelLabel(k))
                              .join(', ')}`}
                          >
                            {channelKeys.map((key) => (
                              <span
                                key={key}
                                className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200"
                              >
                                {channelLabel(key)}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 dark:text-gray-400">{formatDate(r.created_at)}</div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

